import {
  getIndividualPropertySuiteByID,
  getUnitsForBulkOperations,
} from 'src/services/client';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import { ISuite } from 'src/components/SharedTypes';

interface UsePropertySuitesParams {
  propertyId: string;
  userId: string;
  enabled?: boolean;
}

export interface UsePropertySuitesReturn {
  simpleSuitesForWalkthroughDialogue: UnitBulkOperationDto[];
  suites: ISuite[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const usePropertySuitesBulkDialogues = ({
  propertyId,
  userId,
  enabled = true,
}: UsePropertySuitesParams): UsePropertySuitesReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [
    simpleSuitesForWalkthroughDialogue,
    setSimpleSuitesForWalkthroughDialogue,
  ] = useState<UnitBulkOperationDto[]>([]);

  const [suites, setSuites] = useState<ISuite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSimplifiedSuitesListWalkthroughsDialogue = async () => {
    if (!enabled || !propertyId) return;

    try {
      const response: any = await getUnitsForBulkOperations(propertyId);

      if (!response || !response.ok) {
        console.error('Failed to fetch units for bulk operations:', response);
        throw new Error('Failed to load units');
      }

      const suitesFromAPI: UnitBulkOperationDto[] = await response.json();

      const sortedSuitesFromAPI: UnitBulkOperationDto[] = suitesFromAPI.sort(
        (a: UnitBulkOperationDto, b: UnitBulkOperationDto) => {
          const suiteA: string = a.number || '';
          const suiteB: string = b.number || '';

          const suiteNumA: number = parseInt(suiteA, 10);
          const suiteNumB: number = parseInt(suiteB, 10);

          const isNumA: boolean = !isNaN(suiteNumA);
          const isNumB: boolean = !isNaN(suiteNumB);

          // Both are valid numbers - sort numerically.
          if (isNumA && isNumB && suiteNumA !== suiteNumB) {
            return suiteNumA - suiteNumB;
          }

          // If only one is a number, numbers come first.
          if (isNumA && !isNumB) return -1;
          if (!isNumA && isNumB) return 1;

          // Neither are numbers (or numbers were equal) - sort alphabetically.
          return suiteA.localeCompare(suiteB);
        }
      );

      if (!Array.isArray(sortedSuitesFromAPI)) {
        throw new Error('Invalid response format');
      }

      setSimpleSuitesForWalkthroughDialogue(sortedSuitesFromAPI);
    } catch (err) {
      console.error('Error fetching units:', err);
      throw err;
    }
  };

  const fetchSuites = async () => {
    if (!enabled || !propertyId || !userId) return;

    try {
      const userRole = user?.roles[0]?.role?.name;

      if (!userRole) {
        throw new Error('User role not found');
      }

      const response: any = await getIndividualPropertySuiteByID(
        propertyId,
        userId,
        userRole
      );

      if (!response || !response.ok) {
        console.error('Failed to fetch property suites by ID:', response);
        throw new Error('Failed to load suites');
      }

      const suitesFromAPI: ISuite[] = await response.json();

      if (!Array.isArray(suitesFromAPI)) {
        throw new Error('Invalid response format');
      }

      setSuites(suitesFromAPI);
    } catch (err) {
      console.error('Error fetching suites:', err);
      throw err;
    }
  };

  const refetch = async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    /* Enabled but the propertyId hasn't arrived yet (e.g. a deep link opened the dialogue before
     * the property page finished loading). Hold the loading state so consumers render a skeleton
     * instead of empty content; the effect re-runs and fetches once propertyId resolves. */
    if (!propertyId) {
      setIsLoading(true);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await Promise.all([
        fetchSimplifiedSuitesListWalkthroughsDialogue(),
        fetchSuites(),
      ]);
    } catch (err) {
      console.error('Error refetching:', err);
      setError('Error loading data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [propertyId, userId, user?.roles, enabled]);

  return {
    simpleSuitesForWalkthroughDialogue,
    suites,
    isLoading,
    error,
    refetch: refetch,
  };
};
